/**
 * Sources Section Component
 *
 * Displays a collapsible list of documents cited in a message,
 * grouped by document with citation counts.
 * Follows RAGFlow's approach of showing only cited documents.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { SourceDocumentDialog } from './SourceDocumentDialog';
/** Collapses runs of citation numbers: [80, 81, 82, 85] → "80–82, 85". */ export function citationRanges(ids) {
    const sorted = [
        ...new Set(ids)
    ].sort((a, b)=>a - b);
    const parts = [];
    for(let i = 0; i < sorted.length; i++){
        const start = sorted[i];
        while(i + 1 < sorted.length && sorted[i + 1] === sorted[i] + 1)i++;
        parts.push(sorted[i] === start ? String(start) : `${start}–${sorted[i]}`);
    }
    return parts.join(', ');
}
export function SourcesSection({ documents }) {
    const [isExpanded, setIsExpanded] = useState(true);
    const [selectedSource, setSelectedSource] = useState(null);
    if (documents.length === 0) return null;
    const documentCount = documents.length;
    const documentLabel = documentCount === 1 ? 'document' : 'documents';
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-3",
        children: [
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                onClick: ()=>setIsExpanded(!isExpanded),
                className: "inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-[length:var(--fs-ui-caption)] text-muted-foreground transition-colors hover:text-foreground",
                "aria-label": isExpanded ? 'Collapse sources' : 'Expand sources',
                "aria-expanded": isExpanded,
                children: [
                    isExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                        className: "size-3.5",
                        "aria-hidden": "true"
                    }) : /*#__PURE__*/ _jsx(ChevronRight, {
                        className: "size-3.5",
                        "aria-hidden": "true"
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        children: [
                            "Sources · ",
                            documentCount,
                            " ",
                            documentLabel
                        ]
                    })
                ]
            }),
            isExpanded && /*#__PURE__*/ _jsx("ul", {
                className: "mt-1 space-y-0.5 pl-5 text-[length:var(--fs-ui-caption)]",
                children: documents.map((doc, idx)=>/*#__PURE__*/ _jsxs("li", {
                        className: "flex min-w-0 items-baseline gap-3",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "link",
                                size: "inline",
                                disabled: !doc.documentId,
                                onClick: ()=>setSelectedSource(doc),
                                title: doc.name,
                                className: "min-w-0 shrink truncate text-[length:var(--fs-ui-caption)] font-normal text-brand",
                                children: doc.name
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "ml-auto min-w-0 truncate text-right tabular-nums text-muted-foreground",
                                children: citationRanges(doc.citations)
                            })
                        ]
                    }, doc.documentId || idx))
            }),
            selectedSource?.documentId ? /*#__PURE__*/ _jsx(SourceDocumentDialog, {
                documentId: selectedSource.documentId,
                name: selectedSource.name,
                onClose: ()=>setSelectedSource(null)
            }) : null
        ]
    });
}
